/** Scene-only binding; no reader, catalog registration, identity or body inference. */
import type { CharacterState, HomeLife, PetPrivateSummary } from "@/shared/contracts";
import type { MotionSet } from "@/features/pet_motion/manifest";
import { homeMotionPolicy, type HomeFacing, type HomeMotionMode } from "./homeMotionPolicy";
import type { StagePoint } from "./homeStage";

export interface HomeSceneMotionInput {
  petId: string;
  referenceVersion: number;
  bodyRevision: number | null;
  identityGeneration: string;
  motion: MotionSet | null;
  muted?: boolean;
}

export function stageFacing(from: StagePoint, to: StagePoint): HomeFacing | null {
  const dx = to.x - from.x, dy = to.y - from.y;
  if (Math.hypot(dx, dy) < 0.01) return null;
  return Math.abs(dx) > Math.abs(dy) ? dx > 0 ? "right" : "left" : dy > 0 ? "front" : "back";
}

export function sceneMotionBinding(pet: PetPrivateSummary, state: CharacterState | null, input: HomeSceneMotionInput | null) {
  const bound = !!input && !!input.identityGeneration && input.petId === pet.pet_id &&
    state?.pet_id === pet.pet_id && state.active?.pet_id === pet.pet_id &&
    input.referenceVersion === state.active.reference_version;
  const motion = bound ? input.motion : null;
  const identityGeneration = bound ? input.identityGeneration : "";
  const bodyRevision = bound ? input.bodyRevision : null;
  const policy = homeMotionPolicy({ pet, state, motion, bodyRevision, identityGeneration, presence: "home", mode: "free", desiredFacing: "front" });
  // Includes current resource URLs as a capability subscription token, never a
  // fabricated pet generation or a change to the frozen semantic digest.
  const resourceKey = JSON.stringify([policy.identityKey, input?.petId, input?.referenceVersion, motion]);
  return { motion, identityGeneration, bodyRevision, identityKey: policy.identityKey, resourceKey,
    enabled: bound && bodyRevision != null && Number.isSafeInteger(bodyRevision) && bodyRevision >= 0 &&
      motion?.generation_method !== "fixture" && policy.proof === "metadata" && policy.directions.length > 0,
    muted: input?.muted === true };
}

export function sceneMotionMode(life: HomeLife | null, requested: boolean): HomeMotionMode {
  const kind = life?.activity.kind;
  if (kind === "sleeping") return "sleeping";
  if (!kind || kind === "unknown" || kind === "away") return "unknown";
  if (requested) return "moving";
  if (life?.activity.detail?.kind === "eat") return "eating";
  return kind === "awake" && !life.activity.detail ? "free" : "busy";
}
